import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import { LayerType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface ISpacing {
    /**
     * 设置字间距
     * @param layerID - 图层 ID
     * @param value - 字间距（注意：该值 UI 上的值，即底层数据 / 10，在写入图层时需要转为客户端数据）
     * @param options - 配置项
     */
    setLetterSpacing(
        layerID: string,
        value: number,
        options?: { writeHistory: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;

    /**
     * 设置行间距
     * @param layerID - 图层 ID
     * @param value - 行间距（注意：该值 UI 上的值，即底层数据 / 10，在写入图层时需要转为客户端数据）
     * @param options - 配置项
     */
    setLineSpacing(
        layerID: string,
        value: number,
        options?: { writeHistory: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;
}

export class Spacing implements ISpacing {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private calcLayout(layer: ILayerProps) {
        const layerData = layer.data as ITextLayerProps;
        const { writingMode, fontSize, lineSpacing } = layerData;

        const keepConstantWidth =
            writingMode === TextWritingType.HORIZONTAL_TB &&
            (get(layer, '__fixedDirectionSize', false) ||
                checkIsHorizontalMultiLine(layer.height, fontSize, lineSpacing));
        const keepConstantHeight = false;
        const textSize = TextMeasurer.measure(layer, { keepConstantWidth, keepConstantHeight });

        return {
            width: textSize.width,
            height: textSize.height,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        kind: 'lineSpacing' | 'letterSpacing',
        value: number,
        options: { activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = { ...prevLayerData, [kind]: value };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (!shapeExtension) {
            const { width, height } = this.calcLayout(nextLayer);
            nextLayer = { ...nextLayer, width, height };
        } else {
            if (writeHistory) {
                const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
                if (layer) nextLayer = layer;
                updateLayer(this.framework, nextLayer, { activate });
            }
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return nextLayer;
    }

    private async setSpacing(
        layerID: string,
        kind: 'lineSpacing' | 'letterSpacing',
        value: number,
        options = { writeHistory: true, clearPrevLayer: false },
    ) {
        const { writeHistory, clearPrevLayer } = options;

        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.set(child, kind, value, { activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER][kind],
                writeHistory,
                update: !writeHistory,
            });

            if (writeHistory || clearPrevLayer) this.prevLayer = null;

            return nextLayer;
        }

        const nextLayer = await this.set(prevLayer, kind, value, { activate: true, writeHistory });
        if (writeHistory || clearPrevLayer) this.prevLayer = null;

        return nextLayer;
    }

    setLetterSpacing(layerID: string, value: number, options = { writeHistory: true, clearPrevLayer: false }) {
        return this.setSpacing(layerID, 'letterSpacing', value, options);
    }

    setLineSpacing(layerID: string, value: number, options = { writeHistory: true, clearPrevLayer: false }) {
        return this.setSpacing(layerID, 'lineSpacing', value, options);
    }
}
